import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { apiRequest } from "@/lib/queryClient";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import sparLogo from "@/assets/sparlegends.png";
import type { AppInfo } from "@shared/schema";


interface VersionContextType {
  isChecking: boolean;
  needsUpdate: boolean;
  currentVersion: string | null;
}
// Define the current app version
const APP_VERSION = "1.3.4"; // This should be updated when releasing new versions

const VersionContext = createContext<VersionContextType>({
  isChecking: true,
  needsUpdate: false,
  currentVersion: null,
});

export function useVersion() {
  return useContext(VersionContext);
}

// Helper function to compare version strings
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  return 0;
}

interface VersionProviderProps {
  readonly children: ReactNode;
}

export function VersionCheckProvider({ children }: VersionProviderProps) {
  const [countdown, setCountdown] = useState<number | null>(null);
  const [versionInfo, setVersionInfo] = useState<AppInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const data = await apiRequest('GET', '/api/appinfo/current').then(res => res.json());
        setVersionInfo(data);
      } catch (error) {
        console.error('Error fetching version info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersionInfo();
  }, []);

  const handleUpdate = () => {
    if (Capacitor.isNativePlatform()) {
      const url = Capacitor.getPlatform() === "ios"
        ? import.meta.env.VITE_IOS_APP_STORE_URL
        : import.meta.env.VITE_ANDROID_PLAY_STORE_URL;

      if (url) {
        Browser.open({ url });
      }
    } else {
      window.location.reload();
    }
  };

  // Single countdown effect for optional updates
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (versionInfo && !versionInfo.forceUpdate && countdown === null) {
      setCountdown(4);
    }

    if (countdown !== null && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev === null || prev <= 0) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [versionInfo, countdown]);

  const value = React.useMemo(() => ({
    isChecking: isLoading,
    needsUpdate: versionInfo
      ? compareVersions(versionInfo.appVersion, APP_VERSION) > 0
      : false,
    currentVersion: versionInfo?.appVersion ?? null,
  }), [isLoading, versionInfo]);

  const shouldShowVersionCheck = versionInfo && compareVersions(versionInfo.appVersion, APP_VERSION) > 0;

  return (
    <VersionContext.Provider value={value}>
      {!shouldShowVersionCheck ? (
        children
      ) : (
        <>
          {/* Optional Update Dialog with countdown */}
          {!versionInfo.forceUpdate && countdown !== null && countdown > 0 && (
            <div className="fixed inset-0 bg-[#0B3B25] flex flex-col items-center justify-center p-4 z-[9999]">
              <div className="w-full max-w-xs flex flex-col items-center text-center">
                <div className="w-32 h-32 rounded-full overflow-hidden mb-6 bg-black/20">
                  <img 
                    src={sparLogo} 
                    alt="Spar Legends" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h1 className="text-2xl font-semibold text-white mb-2">
                  Update Available
                </h1>
                <p className="text-base text-white/60 mb-4">
                  A new version of the app is available. Open the app store to download.
                </p>
                <p className="text-3xl font-bold text-white mb-8">
                  {countdown}
                </p>
                <Button 
                  onClick={handleUpdate}
                  className={cn(
                    "w-full bg-[#00A551] hover:bg-[#00A551]/90 text-white",
                    "rounded-full py-6 text-lg font-medium"
                  )}
                >
                  Update Now
                </Button>
              </div>
            </div>
          )}

          {/* Force Update Dialog (no countdown) */}
          {versionInfo.forceUpdate && (
            <div className="fixed inset-0 bg-[#0B3B25] flex flex-col items-center justify-center p-4 z-[9999]">
              <div className="w-full max-w-xs flex flex-col items-center text-center">
                <div className="w-32 h-32 rounded-full overflow-hidden mb-6 bg-black/20">
                  <img 
                    src={sparLogo} 
                    alt="Spar Legends" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h1 className="text-2xl font-semibold text-white mb-2">
                  Update Required
                </h1>
                <p className="text-base text-white/60 mb-8">
                  A new version of the app is available. Open the app store to download.
                </p>
                <Button 
                  onClick={handleUpdate}
                  className={cn(
                    "w-full bg-[#00A551] hover:bg-[#00A551]/90 text-white",
                    "rounded-full py-6 text-lg font-medium"
                  )}
                >
                  Update Now
                </Button>
              </div>
            </div>
          )}

          {/* Only render children if it's an optional update and countdown is finished */}
          {(!versionInfo.forceUpdate && (!countdown || countdown <= 0)) && children}
        </>
      )}
    </VersionContext.Provider>
  );
}